import React from "react";

// Real bracelet architecture (matches /app/frontend/src/lib/crystalPrescription.js
// and /app/frontend/src/lib/panchaEngine.js). Total bead count varies by wrist
// size: small = 19, average = 22, large = 25. The diagram below shows a typical
// "average" (22) layout for a customer with a 2-bead Life Path who picked 2
// intentions. Real prescriptions vary — Life Path bead count is 1 (LP1), 2
// (most), or 3 (LP7/LP33), and 1–3 intention pairs (2 primary + 1 support each)
// contribute the rest before Clear Quartz spacers top up to the target.
const BEAD_ORDER = [
  "pancha",   // Earth
  "life",     // LP bead #1
  "life",     // LP bead #2
  "spacer",
  "pancha",   // Water
  "name",     // NN bead #1
  "name",     // NN bead #2
  "spacer",
  "pancha",   // Fire
  "zodiac",   // Rashi bead #1
  "zodiac",   // Rashi bead #2
  "intent",   // Intention 1 primary
  "intent",   // Intention 1 primary
  "intent",   // Intention 1 support
  "spacer",
  "pancha",   // Air
  "intent",   // Intention 2 primary
  "intent",   // Intention 2 primary
  "intent",   // Intention 2 support
  "spacer",
  "pancha",   // Ether
  "spacer",
];

const GROUPS = {
  pancha:  { fill: "#6A0DAD", name: "Pancha Foundation", count: 5,     label: "Your five element beads" },
  life:    { fill: "#D85A30", name: "Life Path",         count: "1-3", label: "Your birth-number crystal" },
  name:    { fill: "#1D9E75", name: "Name Number",       count: 2,     label: "Your name's energy" },
  zodiac:  { fill: "#534AB7", name: "Zodiac",            count: 2,     label: "Your Vedic Rashi stone" },
  intent:  { fill: "#E8B547", name: "Intention Beads",   count: "3-9", label: "Up to 3 healing goals" },
  spacer:  { fill: "#FFFFFF", name: "Clear Quartz",      count: "top up", label: "Sacred spacer beads" },
};

// Container 1040×480. Bead oval centred at (520, 240). Flatter oval (~480×260 in viewBox units → 400×220 visual feel).
const VB_W = 1040;
const VB_H = 480;
const CX = 520;
const CY = 240;
const RX = 240;
const RY = 130;
const N = BEAD_ORDER.length;

function beadPosition(i) {
  const angle = (i / N) * Math.PI * 2 - Math.PI / 2;
  return {
    x: CX + RX * Math.cos(angle),
    y: CY + RY * Math.sin(angle),
  };
}

// One callout per group. labelX/labelY are percentages within the 1040×540 box.
// side determines which side the label sits relative to its leader-line endpoint.
const CALLOUTS = [
  { key: "pancha",  beadIndex: 0,  labelX: 50, labelY: 4,  side: "right" },
  { key: "life",    beadIndex: 2,  labelX: 90, labelY: 20, side: "right" },
  { key: "name",    beadIndex: 6,  labelX: 92, labelY: 55, side: "right" },
  { key: "zodiac",  beadIndex: 10, labelX: 90, labelY: 82, side: "right" },
  { key: "intent",  beadIndex: 13, labelX: 40, labelY: 96, side: "left"  },
  { key: "spacer",  beadIndex: 19, labelX: 8,  labelY: 55, side: "left"  },
];

function LabelBlock({ name, count, label }) {
  return (
    <div style={{ minWidth: "160px" }}>
      <div
        style={{
          fontFamily: "'Cinzel Decorative', Georgia, serif",
          fontSize: "12px",
          color: "#6A0DAD",
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {name} <span style={{ color: "#D4A855" }}>(&times;{count})</span>
      </div>
      <div
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: "11px",
          color: "#6A0DAD",
          marginTop: "2px",
          fontStyle: "italic",
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function BraceletAnatomyDiagram() {
  return (
    <div data-testid="bracelet-anatomy">
      {/* Desktop: SVG oval + HTML overlay labels */}
      <div
        className="hidden md:block relative mx-auto"
        style={{ maxWidth: "1040px", aspectRatio: `${VB_W} / ${VB_H}`, width: "100%" }}
      >
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%", display: "block" }}>
          {/* Leader lines from each callout bead to its label anchor (just outside the bead, towards the label corner) */}
          {CALLOUTS.map(({ key, beadIndex, labelX, labelY }) => {
            const bead = beadPosition(beadIndex);
            const lx = (labelX / 100) * VB_W;
            const ly = (labelY / 100) * VB_H;
            return (
              <line
                key={`line-${key}`}
                x1={bead.x}
                y1={bead.y}
                x2={lx}
                y2={ly}
                stroke="#D4A855"
                strokeWidth={1}
              />
            );
          })}

          {/* Beads */}
          {BEAD_ORDER.map((key, i) => {
            const { x, y } = beadPosition(i);
            const g = GROUPS[key];
            return (
              <circle
                key={`bead-${i}`}
                className="bracelet-anatomy-bead"
                cx={x}
                cy={y}
                r={12}
                fill={g.fill}
                stroke={key === "spacer" ? "#D4A855" : "none"}
                strokeWidth={key === "spacer" ? 2 : 0}
              />
            );
          })}
        </svg>

        {/* Center size range — HTML overlay */}
        <div
          className="absolute pointer-events-none text-center"
          style={{
            left: `${(CX / VB_W) * 100}%`,
            top: `${(CY / VB_H) * 100}%`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <div style={{ fontFamily: "'Cinzel Decorative', Georgia, serif", fontSize: "22px", fontWeight: 700, color: "#6A0DAD", lineHeight: 1 }}>
            19 &middot; 22 &middot; 25
          </div>
          <div
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "13px",
              color: "#888888",
              fontStyle: "italic",
              marginTop: "4px",
              maxWidth: "180px",
            }}
          >
            beads &mdash; sized to your wrist
          </div>
        </div>

        {/* HTML labels overlaid at the end of each leader line */}
        {CALLOUTS.map(({ key, labelX, labelY, side }) => {
          const g = GROUPS[key];
          const translate = side === "left" ? "translate(-100%, -50%)" : "translate(0, -50%)";
          const padding = side === "left" ? "0 8px 0 0" : "0 0 0 8px";
          return (
            <div
              key={`label-${key}`}
              className="absolute"
              style={{
                left: `${labelX}%`,
                top: `${labelY}%`,
                transform: translate,
                padding,
                textAlign: side === "left" ? "right" : "left",
              }}
            >
              <LabelBlock name={g.name} count={g.count} label={g.label} />
            </div>
          );
        })}
      </div>

      {/* Mobile: linear bead row + legend + size caption */}
      <div className="block md:hidden">
        <div className="overflow-x-auto py-4" data-testid="bracelet-anatomy-mobile-row">
          <svg viewBox="0 0 720 60" width="100%" style={{ minWidth: "560px" }} aria-hidden>
            {BEAD_ORDER.map((key, i) => {
              const g = GROUPS[key];
              const cx = 20 + i * ((720 - 40) / (N - 1));
              const cy = 30 + Math.sin((i / N) * Math.PI * 2) * 6;
              return (
                <circle
                  key={`mbead-${i}`}
                  cx={cx}
                  cy={cy}
                  r={11}
                  fill={g.fill}
                  stroke={key === "spacer" ? "#D4A855" : "none"}
                  strokeWidth={key === "spacer" ? 2 : 0}
                />
              );
            })}
          </svg>
        </div>
        <div className="text-center mt-2">
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "#6A0DAD" }}>
            19 &middot; 22 &middot; 25
          </span>{" "}
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 13, color: "#888", fontStyle: "italic" }}>
            beads &mdash; sized to your wrist
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5 max-w-md mx-auto">
          {Object.entries(GROUPS).map(([key, g]) => (
            <div key={key} className="flex items-center gap-2 text-left" data-testid={`legend-${key}`}>
              <span
                className="inline-block shrink-0"
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: g.fill,
                  border: key === "spacer" ? "2px solid #D4A855" : "none",
                }}
              />
              <div>
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 12,
                    color: "#6A0DAD",
                    letterSpacing: "0.12em",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  {g.name}
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 11, color: "#444", fontStyle: "italic" }}>
                  &times;{g.count} &middot; {g.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
